import { type ReactNode, type RefObject, useId, useRef } from "react";
import { createPortal } from "react-dom";
import type { IconName } from "../../icons";
import { ModalHeader } from "./ModalHeader";
import { backdropClass, dialogClass, dialogRole, type ModalSize, type ModalTone, type ModalVariant } from "./modalClasses";
import { useModalBehaviour } from "./useModalBehaviour";

export interface ModalProps {
  open: boolean;
  title: string;
  description?: ReactNode;
  icon?: IconName;
  tone?: ModalTone;
  size?: ModalSize;
  onClose: () => void;
  children?: ReactNode;
  actions?: ReactNode;
  /** Clicking the backdrop closes (default true). */
  dismissible?: boolean;
  initialFocusRef?: RefObject<HTMLElement | null>;
  /** "drawer" slides in from the right, full height (detail panels). */
  variant?: ModalVariant;
  /** Extra content in the header, next to the close button. */
  headerActions?: ReactNode;
}

/** Accessible dialog: focus trap, Escape, focus restore, scroll lock. */
export function Modal({
  open,
  title,
  description,
  icon,
  tone = "default",
  size = "md",
  onClose,
  children,
  actions,
  dismissible = true,
  initialFocusRef,
  variant = "dialog",
  headerActions,
}: ModalProps): React.JSX.Element | null {
  const dialogRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const descriptionId = useId();
  useModalBehaviour({ open, dialogRef, initialFocusRef, onClose });

  if (!open) return null;

  return createPortal(
    <div
      className={backdropClass(variant)}
      role="presentation"
      onMouseDown={(e) => {
        if (dismissible && e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        className={dialogClass(size, variant)}
        role={dialogRole(tone)}
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
      >
        <ModalHeader
          title={title}
          titleId={titleId}
          description={description}
          descriptionId={descriptionId}
          icon={icon}
          tone={tone}
          headerActions={headerActions}
          onClose={onClose}
        />
        {children ? <div className="nb-modal-body">{children}</div> : null}
        {actions ? <div className="nb-modal-foot">{actions}</div> : null}
      </div>
    </div>,
    document.body,
  );
}
